import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import axiosWrapper from "@AxiosWrapper";
import { portalPurchaseUnitsUrl } from "@Services/dataManager";
import type { GetPurchaseUnitsResponse, GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
type PurchaseUnitItem = NonNullable<GetPurchaseUnitsResponse["result"]>[number];
type MeasurementUnitItem = NonNullable<GetMeasurementUnitsResponse["result"]>[number];
import TreeSelectDropDown from "@CubeComponents/html/TreeDropDown";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import _ from "lodash";
import { useTranslation } from "@i18n";
import { useEffect, useMemo, useState } from "react";

/**
 * onChange args from NumericTextBox forwarded to updateItemDetails.
 * The parent reads `.value` and dispatches the update.
 */
type UpdateItemDetailsArgs = {
  value: number | null;
  previousValue?: number | null;
  event?: Event;
  target: { value: string; name?: string };
};

type BoqItemUnitDetailsProps = {
  itemCurrentDetails: {
    code?: string;
    hasChild?: boolean;
    quantityFromTakeoffs?: number;
    purchaseUnit?: string;
    purchaseUnitName?: string;
    conversionRateMU?: number | string;
    conversionRatePU?: number | string;
    measurementUnit?: string;
  };
  updateItemDetails: (
    args: UpdateItemDetailsArgs | { itemData?: MeasurementUnitItem; value: string[] } | { value: PurchaseUnitItem },
    field?: string,
    checkNumberIsPositive?: boolean,
  ) => void;
  isActionPermission: boolean;
  measurementUnitsDataSource: MeasurementUnitItem[];
};

/** Flatten nested measurement units into tree-compatible flat list with id/label/parentId */
function flattenMeasurementUnitsForTree(
  data: MeasurementUnitItem[],
): Array<{ id: string; label: string; parentId: string | null; selectable: boolean }> {
  const result: Array<{ id: string; label: string; parentId: string | null; selectable: boolean }> = [];
  for (const item of data) {
    const parentId = item._id ?? "";
    const parentName = item.name ?? "";
    const children = (item as MeasurementUnitItem & { units?: MeasurementUnitItem[] }).units;
    result.push({ id: parentId, label: parentName, parentId: null, selectable: false });
    if (children && children.length > 0) {
      for (const child of children) {
        result.push({
          id: child._id ?? "",
          label: child.name ?? "",
          parentId,
          selectable: true,
        });
      }
    }
  }
  return result;
}

const BoqItemUnitDetails = ({
  itemCurrentDetails,
  updateItemDetails,
  isActionPermission,
  measurementUnitsDataSource,
}: BoqItemUnitDetailsProps) => {
  const { t } = useTranslation();
  const [purchaseUnitsOptions, setPurchaseUnitsOptions] = useState<PurchaseUnitItem[]>([]);

  useEffect(() => {
    const fetchPurchaseUnits = async () => {
      const response = await axiosWrapper.post<GetPurchaseUnitsResponse>(portalPurchaseUnitsUrl, {
        value: { params: {} },
      });
      if (response.data?.result) {
        setPurchaseUnitsOptions(response.data.result);
      }
    };
    fetchPurchaseUnits();
  }, []);

  const treeDataMeasurementUnits = useMemo(
    () => flattenMeasurementUnitsForTree(measurementUnitsDataSource),
    [measurementUnitsDataSource],
  );

  const resolvedMeasurementUnitLabel = useMemo(() => {
    const found = treeDataMeasurementUnits.find((u) => u.id === itemCurrentDetails.measurementUnit);
    return found?.label ?? "";
  }, [treeDataMeasurementUnits, itemCurrentDetails.measurementUnit]);

  return (
    <div className="flex flex-col gap-6 py-6 w-full">
      <div className="flex flex-col gap-4 pb-4">
        <div className="flex gap-5">
          <span className="w-40 text-base font-semibold">{t("Purchase Unit")}</span>
          <AutoComplete
            disableClearable
            options={purchaseUnitsOptions}
            valueField="_id"
            getOptionLabel={(option) => option?.nameWithSymbol || ""}
            value={itemCurrentDetails.purchaseUnit ?? undefined}
            disabled={
              Boolean(itemCurrentDetails.code) ||
              itemCurrentDetails.hasChild ||
              Boolean(itemCurrentDetails.quantityFromTakeoffs) ||
              !isActionPermission
            }
            onChange={(_event, newValue) => {
              if (newValue?._id) {
                updateItemDetails({ value: newValue }, "purchaseUnit");
              }
            }}
            sx={{ width: "12.5rem" }}
            renderInput={(params) => <TextFieldComponent {...params} />}
          />
        </div>
      </div>

      {_.get(itemCurrentDetails, "purchaseUnit") && !itemCurrentDetails.hasChild && (
        <div className="flex flex-col gap-6">
          <div className="text-xl font-semibold">{t("Relation")}</div>
          <div className="flex gap-6">
            <div className="flex flex-col gap-8 p-4 border border-button_light rounded">
              <span className="text-base font-semibold">{t("Purchase Unit")}</span>
              <div className="flex gap-6 items-center">
                <NumericTextBoxComponent
                  width="4.375rem"
                  value={
                    itemCurrentDetails.conversionRateMU !== undefined
                      ? Number(itemCurrentDetails.conversionRateMU)
                      : undefined
                  }
                  showSpinButton={false}
                  onChange={(args: UpdateItemDetailsArgs) => updateItemDetails(args, "conversionRateMU", true)}
                  disabled={!isActionPermission}
                />
                <span className="text-base font-semibold">{itemCurrentDetails.purchaseUnitName}</span>
              </div>
            </div>
            <div className="flex items-center justify-center w-24">=</div>
            <div className="flex flex-col gap-8 p-4 border border-button_light rounded">
              <span className="text-base font-semibold">{t("Measurement Unit")}</span>
              <div className="flex gap-6">
                <NumericTextBoxComponent
                  width="4.375rem"
                  value={
                    itemCurrentDetails.conversionRatePU !== undefined
                      ? Number(itemCurrentDetails.conversionRatePU)
                      : undefined
                  }
                  showSpinButton={false}
                  onChange={(args: UpdateItemDetailsArgs) => updateItemDetails(args, "conversionRatePU", true)}
                  disabled={!isActionPermission}
                />
                <TreeSelectDropDown
                  treeData={treeDataMeasurementUnits}
                  value={resolvedMeasurementUnitLabel}
                  disabled={!isActionPermission || Number.isFinite(itemCurrentDetails.quantityFromTakeoffs)}
                  disabledParentSelection
                  allowDeselection={false}
                  onSelectionChange={(selected) =>
                    updateItemDetails({ itemData: selected, value: [selected?.id] }, "measurementUnit")
                  }
                  inputFieldHeight={"2rem"}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoqItemUnitDetails;
